import React from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  ChevronLeft,
  KeyRound,
  Lock,
  LockOpen,
  Mail,
  MoreVertical,
  Plus,
  RefreshCw,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { FeatureLimitHint } from '../../../../components/FeatureLimitHint';
import { InlineLoadingState, LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useFeatureLimits } from '../../../../hooks/useFeatureLimits';
import { DatePicker } from '../../../../components/DatePicker';
import { AppSelectDropdown } from '../../../../components/AppSelectDropdown';
import {
  emptyMailDispatchSettings,
  MAIL_DISPATCH_PURPOSES,
  MAIL_DISPATCH_PURPOSE_LABELS,
  MAIL_DISPATCH_PURPOSE_SHORT_LABELS,
  parseMailDispatchSettings,
  addressMatchesDispatch,
  type MailDispatchConfig,
  type MailDispatchPurpose,
  type MailDispatchSettings,
} from '../../../../types/mailDispatch';
import { fetchMe } from '../../../../lib/meClient';
import { SettingsCard } from '../../components/SettingsCard';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../../utils/settingsSectionStyles';
import type { InboxAccount } from './types';
import { MailboxConnectForm } from './MailboxConnectForm';
import { mailboxFromChoices } from '../../../inbox/mailboxFromChoices';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';

type Props = {
  accounts: InboxAccount[];
  loading: boolean;
  onReload: () => void;
  onError: (message: string | null) => void;
};

type CardView = 'list' | 'add' | 'dispatch';
type ImapConnectStage = 'idle' | 'lookup-domain' | 'lookup-provider' | 'connect-mailbox';

function isGmailProvider(provider: string | undefined | null): boolean {
  const p = String(provider || '').toLowerCase();
  return p === 'gmail' || p === 'google';
}

function isImapProvider(provider: string | undefined | null): boolean {
  return String(provider || '').toLowerCase() === 'imap';
}

function providerLabel(provider: string | undefined | null): string {
  if (isGmailProvider(provider)) return 'Gmail';
  if (isImapProvider(provider)) return 'IMAP';
  return String(provider || 'E-Mail');
}

function isAccountConnected(acc: InboxAccount): boolean {
  return Boolean(acc.nylas_grant_id) || Boolean(acc.is_demo_simulated_connection);
}

const emptyImapForm = () => ({
  email: '',
  password: '',
  showAdvanced: false,
  imap_host: '',
  imap_port: '993',
  smtp_host: '',
  smtp_port: '587',
});

export function EmailInboxSettingsCard({ accounts, loading, onReload, onError }: Props) {
  // Synchronisieren füllt nur das gemeinsame Postfach; ohne Posteingang sperrt der Server /sync.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const inboxEnabled = modulesLoaded && isEnabled('posteingang');
  const [syncingId, setSyncingId] = React.useState<string | number | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [removeId, setRemoveId] = React.useState<string | number | null>(null);
  const [cardView, setCardView] = React.useState<CardView>('list');
  const [imapForm, setImapForm] = React.useState(emptyImapForm());
  const [imapConnectStage, setImapConnectStage] = React.useState<ImapConnectStage>('idle');
  const [reauthImapAccount, setReauthImapAccount] = React.useState<InboxAccount | null>(null);
  const [privacyConfirmAccount, setPrivacyConfirmAccount] = React.useState<InboxAccount | null>(null);
  const [privacyReplacementAccountId, setPrivacyReplacementAccountId] = React.useState<string>('');
  const [visibilitySaving, setVisibilitySaving] = React.useState(false);
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null);
  const menuTriggerRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
  const menuPanelRef = React.useRef<HTMLDivElement | null>(null);

  const { usage, loaded: limitsReady } = useFeatureLimits();
  const mailboxLimit = usage('inboxAccounts');
  const mailboxesAtLimit = accounts.length >= mailboxLimit.max;
  const [showSyncSinceModal, setShowSyncSinceModal] = React.useState(false);
  const [syncSinceDate, setSyncSinceDate] = React.useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });
  const [pendingSyncAccountId, setPendingSyncAccountId] = React.useState<string | number | null>(null);

  const [mailDispatchLoaded, setMailDispatchLoaded] = React.useState(false);
  const [mailDispatchSaving, setMailDispatchSaving] = React.useState(false);
  const [mailDispatchError, setMailDispatchError] = React.useState<string | null>(null);
  const [mailDispatchWarnings, setMailDispatchWarnings] = React.useState<string[]>([]);
  const [mailDispatchDraft, setMailDispatchDraft] = React.useState<MailDispatchConfig>(emptyMailDispatchSettings);
  const [dispatchAccountId, setDispatchAccountId] = React.useState<string | null>(null);
  const [dispatchEmail, setDispatchEmail] = React.useState<string | null>(null);
  const mailDispatchDraftBaselineRef = React.useRef<MailDispatchConfig>(emptyMailDispatchSettings());

  const loadMailDispatchSettings = React.useCallback(() => {
    setMailDispatchLoaded(false);
    setMailDispatchError(null);
    fetch('/api/settings/mail-dispatch', { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error((d as { error?: string }).error || 'Einstellungen konnten nicht geladen werden.');
        return d as {
          settings?: MailDispatchConfig;
          warnings?: string[];
        };
      })
      .then((data) => {
        setMailDispatchDraft(parseMailDispatchSettings(data.settings));
        setMailDispatchWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      })
      .catch((e) => {
        setMailDispatchError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.');
      })
      .finally(() => setMailDispatchLoaded(true));
  }, []);

  React.useEffect(() => {
    loadMailDispatchSettings();
  }, [loadMailDispatchSettings]);

  const updateMenuPosition = React.useCallback(() => {
    if (!openMenuId) {
      setMenuPos(null);
      return;
    }
    const el = menuTriggerRefs.current.get(openMenuId);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 220;
    const margin = 8;
    let left = rect.right - width;
    if (left < margin) left = margin;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    const panelHeight = menuPanelRef.current?.offsetHeight ?? 200;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const top =
      spaceBelow < panelHeight && rect.top > panelHeight + margin
        ? rect.top - panelHeight - 4
        : rect.bottom + 4;
    setMenuPos((prev) => {
      if (prev && prev.top === top && prev.left === left) return prev;
      return { top, left };
    });
  }, [openMenuId]);

  React.useLayoutEffect(() => {
    if (!openMenuId) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = (e: Event) => {
      const t = e.target as Node | null;
      if (t && menuPanelRef.current?.contains(t)) return;
      updateMenuPosition();
    };
    window.addEventListener('resize', updateMenuPosition);
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openMenuId, updateMenuPosition]);

  // Remeasure after the portaled panel mounts so flip uses the real height.
  React.useLayoutEffect(() => {
    if (!openMenuId || !menuPos || !menuPanelRef.current) return;
    updateMenuPosition();
  }, [openMenuId, menuPos, updateMenuPosition]);

  React.useEffect(() => {
    if (!openMenuId) return;
    const handlePointerDown = (event: MouseEvent) => {
      const t = event.target as Node;
      if (menuPanelRef.current?.contains(t)) return;
      const trigger = menuTriggerRefs.current.get(openMenuId);
      if (trigger?.contains(t)) return;
      setOpenMenuId(null);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openMenuId]);

  React.useEffect(() => {
    let cancelled = false;
    void fetchMe()
      .then((me) => {
        if (cancelled) return;
        setCurrentUserId(me?.user?.id ? String(me.user.id) : null);
      })
      .catch(() => {
        if (!cancelled) setCurrentUserId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const newestConnectedGeneralId = React.useMemo(() => {
    const connected = accounts.filter((acc) => isAccountConnected(acc) && !acc.is_private);
    if (connected.length === 0) return null;
    let latest = connected[0];
    for (const acc of connected) {
      if (new Date(acc.created_at).getTime() > new Date(latest.created_at).getTime()) {
        latest = acc;
      }
    }
    return String(latest.id);
  }, [accounts]);

  const standardAccountId = React.useMemo(() => {
    const connectedIds = new Set(
      accounts
        .filter((acc) => isAccountConnected(acc) && !acc.is_private)
        .map((acc) => String(acc.id)),
    );
    if (mailDispatchDraft.default && connectedIds.has(mailDispatchDraft.default)) {
      return mailDispatchDraft.default;
    }
    return newestConnectedGeneralId;
  }, [accounts, mailDispatchDraft.default, newestConnectedGeneralId]);

  const addressIsStandard = React.useCallback(
    (accountId: string, email: string, primary: string, isPrivate: boolean) => {
      if (isPrivate || standardAccountId !== accountId) return false;
      const explicit = mailDispatchDraft.default === accountId;
      const stored = String(mailDispatchDraft.from?.default ?? '').trim().toLowerCase();
      if (explicit && stored) return stored === email.trim().toLowerCase();
      return email.trim().toLowerCase() === primary.trim().toLowerCase();
    },
    [mailDispatchDraft.default, mailDispatchDraft.from, standardAccountId],
  );

  const dispatchAccount = React.useMemo(
    () => (dispatchAccountId ? accounts.find((a) => String(a.id) === dispatchAccountId) ?? null : null),
    [accounts, dispatchAccountId],
  );

  const openMenuTarget = React.useMemo(() => {
    if (!openMenuId) return null;
    const [accountId, aliasFlag, ...emailParts] = openMenuId.split('::');
    const account = accounts.find((a) => String(a.id) === accountId) ?? null;
    if (!account) return null;
    const email = (emailParts.join('::') || account.email).trim().toLowerCase();
    return {
      account,
      email,
      isAlias: aliasFlag === 'alias',
    };
  }, [accounts, openMenuId]);
  const openMenuAccount = openMenuTarget?.account ?? null;

  const setMailDispatchPurposeForAccount = React.useCallback(
    (accountId: string, primary: string, email: string, purpose: MailDispatchPurpose, active: boolean) => {
      setMailDispatchDraft((prev) => {
        const owns = addressMatchesDispatch(prev, purpose, accountId, email, primary);
        return {
          ...prev,
          [purpose]: active ? accountId : owns ? null : prev[purpose],
          from: {
            ...prev.from,
            [purpose]: active ? email : owns ? null : prev.from?.[purpose],
          },
        };
      });
    },
    [],
  );

  const beginMailDispatchForAccount = React.useCallback(
    (accountId: string, email: string) => {
      mailDispatchDraftBaselineRef.current = {
        ...mailDispatchDraft,
        from: { ...mailDispatchDraft.from },
      };
      setMailDispatchError(null);
      setDispatchAccountId(accountId);
      setDispatchEmail(email);
      setOpenMenuId(null);
      setCardView('dispatch');
    },
    [mailDispatchDraft],
  );

  const cancelMailDispatchEdit = React.useCallback(() => {
    setMailDispatchDraft({ ...mailDispatchDraftBaselineRef.current });
    setDispatchAccountId(null);
    setDispatchEmail(null);
    setCardView('list');
    setMailDispatchError(null);
  }, []);

  const persistMailDispatchSettings = async (settings: MailDispatchSettings): Promise<boolean> => {
    setMailDispatchSaving(true);
    setMailDispatchError(null);
    try {
      const res = await fetch('/api/settings/mail-dispatch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ settings }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Speichern fehlgeschlagen');
      const next = (d as { settings?: MailDispatchConfig }).settings;
      if (next) setMailDispatchDraft(parseMailDispatchSettings(next));
      setMailDispatchWarnings(
        Array.isArray((d as { warnings?: string[] }).warnings) ? (d as { warnings: string[] }).warnings : [],
      );
      return true;
    } catch (e) {
      setMailDispatchError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
      return false;
    } finally {
      setMailDispatchSaving(false);
    }
  };

  const saveMailDispatchSettings = async (): Promise<boolean> => persistMailDispatchSettings(mailDispatchDraft);

  const handleSetAsStandard = async (acc: InboxAccount, email: string) => {
    setOpenMenuId(null);
    const previous = mailDispatchDraft;
    const defaultId = String(acc.id);
    const fromEmail = email.trim().toLowerCase();
    const next = {
      ...previous,
      default: defaultId,
      from: { ...previous.from, default: fromEmail },
    };
    setMailDispatchDraft(next);
    const ok = await persistMailDispatchSettings({ default: defaultId, from: { default: fromEmail } });
    if (!ok) setMailDispatchDraft(previous);
  };

  const handleSaveMailDispatchAndClose = async () => {
    const ok = await saveMailDispatchSettings();
    if (ok) {
      setDispatchAccountId(null);
      setDispatchEmail(null);
      setCardView('list');
    }
  };

  const openAddForm = () => {
    if (accounts.length >= mailboxLimit.max) return;
    setAdding(false);
    setOpenMenuId(null);
    setCardView('add');
  };

  const resetAddForm = () => {
    setCardView('list');
    setAdding(false);
  };

  const promptSyncSinceForAccount = (accountId: string | number) => {
    if (!inboxEnabled) return;
    setPendingSyncAccountId(accountId);
    setShowSyncSinceModal(true);
  };

  const applyAccountVisibility = async (
    acc: InboxAccount,
    isPrivate: boolean,
    options?: { replacementAccountId?: string | null },
  ) => {
    setVisibilitySaving(true);
    onError(null);
    try {
      const replacementId = String(options?.replacementAccountId ?? '').trim();
      const res = await fetch(`/api/inbox/accounts/${encodeURIComponent(String(acc.id))}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          is_private: isPrivate,
          replacement_account_id: isPrivate && replacementId ? replacementId : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Sichtbarkeit konnte nicht geändert werden.');

      setPrivacyConfirmAccount(null);
      setPrivacyReplacementAccountId('');
      setOpenMenuId(null);
      loadMailDispatchSettings();
      onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Sichtbarkeit konnte nicht geändert werden.');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const requestVisibilityChange = (acc: InboxAccount) => {
    const nextPrivate = !Boolean(acc.is_private);
    setOpenMenuId(null);
    if (nextPrivate) {
      const alternatives = accounts.filter(
        (a) =>
          String(a.id) !== String(acc.id) &&
          !a.is_private &&
          isAccountConnected(a),
      );
      setPrivacyReplacementAccountId(alternatives[0] ? String(alternatives[0].id) : '');
      setPrivacyConfirmAccount(acc);
      return;
    }
    void applyAccountVisibility(acc, false);
  };

  const privacyAffectedPurposes = React.useMemo(() => {
    if (!privacyConfirmAccount || !mailDispatchLoaded) return [] as MailDispatchPurpose[];
    const id = String(privacyConfirmAccount.id);
    return MAIL_DISPATCH_PURPOSES.filter((purpose) => mailDispatchDraft[purpose] === id);
  }, [privacyConfirmAccount, mailDispatchLoaded, mailDispatchDraft]);

  const privacyIsExplicitDefault = Boolean(
    privacyConfirmAccount && mailDispatchDraft.default === String(privacyConfirmAccount.id),
  );
  const privacyNeedsReplacement = privacyAffectedPurposes.length > 0 || privacyIsExplicitDefault;

  const privacyReplacementOptions = React.useMemo(() => {
    if (!privacyConfirmAccount) return [] as InboxAccount[];
    return accounts.filter(
      (a) =>
        String(a.id) !== String(privacyConfirmAccount.id) &&
        !a.is_private &&
        isAccountConnected(a),
    );
  }, [accounts, privacyConfirmAccount]);

  const handleReauthenticateGoogle = (acc: InboxAccount) => {
    setAdding(true);
    onError(null);
    fetch('/api/inbox/oauth/google/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId: String(acc.id) }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'OAuth-Start fehlgeschlagen');
        if (!d?.authUrl) throw new Error('Keine OAuth-URL erhalten');
        window.location.href = d.authUrl;
      })
      .catch((err) => {
        onError(err.message || 'OAuth-Start fehlgeschlagen');
        setAdding(false);
      });
  };

  const handleConnectImap = async (options?: { reauthAccount?: InboxAccount }) => {
    const reauthAccount = options?.reauthAccount;
    const email = reauthAccount ? String(reauthAccount.email || '').trim() : imapForm.email.trim();
    if (!email) {
      onError('Bitte eine E-Mail-Adresse eingeben.');
      return;
    }
    if (!imapForm.password) {
      onError('Bitte ein Passwort eingeben.');
      return;
    }
    setAdding(true);
    onError(null);
    setImapConnectStage('lookup-domain');
    const stageTimers = [
      window.setTimeout(() => setImapConnectStage('lookup-provider'), 450),
      window.setTimeout(() => setImapConnectStage('connect-mailbox'), 1100),
    ];

    const body: Record<string, unknown> = {
      password: imapForm.password,
    };
    if (reauthAccount) {
      body.accountId = String(reauthAccount.id);
    } else {
      body.email = email.toLowerCase();
    }
    if (imapForm.showAdvanced) {
      if (imapForm.imap_host.trim()) body.imap_host = imapForm.imap_host.trim();
      if (imapForm.smtp_host.trim()) body.smtp_host = imapForm.smtp_host.trim();
      if (imapForm.imap_port.trim()) body.imap_port = Number(imapForm.imap_port);
      if (imapForm.smtp_port.trim()) body.smtp_port = Number(imapForm.smtp_port);
    }

    try {
      const res = await fetch('/api/inbox/imap/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Verbindung fehlgeschlagen');

      const account = data as InboxAccount;
      if (reauthAccount) {
        setReauthImapAccount(null);
        setImapForm(emptyImapForm());
        setImapConnectStage('idle');
        onReload();
        return;
      }

      resetAddForm();
      onReload();
      if (account?.id != null && account.sync_since == null) {
        promptSyncSinceForAccount(account.id);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Verbindung fehlgeschlagen');
    } finally {
      stageTimers.forEach((timer) => window.clearTimeout(timer));
      setImapConnectStage('idle');
      setAdding(false);
    }
  };

  const runSync = (accountId: string | number, since?: string) => {
    setSyncingId(accountId);
    onError(null);
    const body = since ? JSON.stringify({ since }) : undefined;
    fetch(`/api/inbox/accounts/${accountId}/sync`, {
      method: 'POST',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body,
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'Sync fehlgeschlagen');
      })
      .catch((err) => onError(err.message || 'Sync fehlgeschlagen'))
      .finally(() => {
        setSyncingId(null);
        onReload();
      });
  };

  const handleSync = (accountId: string | number) => {
    const account = accounts.find((a) => String(a.id) === String(accountId));
    const hasSyncSince = account && account.sync_since != null;
    if (!hasSyncSince) {
      promptSyncSinceForAccount(accountId);
      return;
    }
    runSync(accountId);
  };

  const handleSyncSinceConfirm = () => {
    if (pendingSyncAccountId == null) return;
    runSync(pendingSyncAccountId, syncSinceDate);
    setShowSyncSinceModal(false);
    setPendingSyncAccountId(null);
  };

  const handleCredentialsChange = (acc: InboxAccount) => {
    setOpenMenuId(null);
    if (isGmailProvider(acc.provider)) {
      handleReauthenticateGoogle(acc);
      return;
    }
    if (isImapProvider(acc.provider)) {
      setImapForm(emptyImapForm());
      setReauthImapAccount(acc);
    }
  };

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && removeId !== null) {
        setRemoveId(null);
      }
    };
    if (removeId !== null) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [removeId]);

  const cardTitle =
    cardView === 'dispatch' ? 'Versandart festlegen' : cardView === 'add' ? 'Postfach hinzufügen' : 'Verbundene Postfächer';

  return (
    <>
      <SettingsCard
        title={cardTitle}
        descriptionClassName="mb-2 text-sm text-app-text-muted"
        description={
          cardView === 'list' ? (
            <>
              E-Mail-Postfächer für Versand und Posteingang (Gmail oder IMAP-Anbieter wie IONOS, Strato). Weisen
              Sie einem Postfach über das Menü eine{' '}
              <span className="font-medium text-pipedrive-blue">Versandart</span> zu. Nicht zugewiesene Arten
              nutzen das Standard-Postfach — das legen Sie über das Menü fest.
            </>
          ) : undefined
        }
        leading={
          cardView === 'list' ? undefined : (
            <button
              type="button"
              onClick={cardView === 'add' ? resetAddForm : cancelMailDispatchEdit}
              className="rounded-lg p-1.5 text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted transition-colors"
              aria-label="Zurück"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
            </button>
          )
        }
        actions={
          cardView === 'list' ? (
            <>
              <FeatureLimitHint
                limitKey="inboxAccounts"
                used={accounts.length}
                max={mailboxLimit.max}
                ready={limitsReady}
              />
              <button
                type="button"
                onClick={openAddForm}
                disabled={mailboxesAtLimit}
                className={`${SETTINGS_BTN_PRIMARY} disabled:opacity-40`}
              >
                <Plus className="w-4 h-4" />
                Postfach hinzufügen
              </button>
            </>
          ) : cardView === 'dispatch' ? (
            <>
              <button
                type="button"
                onClick={cancelMailDispatchEdit}
                disabled={mailDispatchSaving}
                className={SETTINGS_BTN_OUTLINE}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void handleSaveMailDispatchAndClose()}
                disabled={mailDispatchSaving}
                className={SETTINGS_BTN_PRIMARY}
              >
                {mailDispatchSaving ? (
                  <>
                    <LoadingSpinner />
                    Speichert…
                  </>
                ) : (
                  'Speichern'
                )}
              </button>
            </>
          ) : undefined
        }
      >
        {mailDispatchError ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {mailDispatchError}
          </div>
        ) : null}
        {mailDispatchWarnings.length > 0 && cardView === 'list' ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 space-y-1">
            {mailDispatchWarnings.map((w) => (
              <p key={w}>{w}</p>
            ))}
          </div>
        ) : null}

        {cardView === 'dispatch' && dispatchAccount ? (
          <>
            <div className="space-y-5">
              <div className="flex items-center gap-2 rounded-lg bg-app-surface-muted px-3 py-2.5">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    isAccountConnected(dispatchAccount) ? 'bg-emerald-500' : 'bg-amber-400'
                  }`}
                />
                <span className="text-sm font-medium text-app-text truncate">
                  {dispatchEmail || dispatchAccount.email}
                </span>
              </div>
              <div>
                <p className={`${SETTINGS_FIELD_LABEL} mb-2`}>Versandart</p>
                <div className="flex flex-wrap gap-2">
                  {MAIL_DISPATCH_PURPOSES.map((purpose) => {
                    const email = dispatchEmail || dispatchAccount.email;
                    const active = addressMatchesDispatch(
                      mailDispatchDraft,
                      purpose,
                      String(dispatchAccount.id),
                      email,
                      dispatchAccount.email,
                    );
                    return (
                      <button
                        key={purpose}
                        type="button"
                        onClick={() =>
                          setMailDispatchPurposeForAccount(
                            String(dispatchAccount.id),
                            dispatchAccount.email,
                            email,
                            purpose,
                            !active,
                          )
                        }
                        title={MAIL_DISPATCH_PURPOSE_LABELS[purpose]}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          active
                            ? 'border-pipedrive-blue bg-pipedrive-blue text-white'
                            : 'border-pipedrive-blue/40 bg-app-surface text-pipedrive-blue hover:bg-blue-50/50'
                        }`}
                      >
                        {MAIL_DISPATCH_PURPOSE_SHORT_LABELS[purpose]}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-app-text-subtle">
                  Wähle die Belegarten aus, für die diese Adresse automatisch als Absender verwendet wird.
                </p>
              </div>
            </div>
          </>
        ) : cardView === 'add' ? (
          <MailboxConnectForm
            variant="embedded"
            onCancel={resetAddForm}
            onImapConnected={(account) => {
              resetAddForm();
              onReload();
              if (account?.id != null && account.sync_since == null) {
                promptSyncSinceForAccount(account.id);
              }
            }}
            onError={onError}
          />
        ) : (
          <div>
            {loading ? (
              <InlineLoadingState label="Lade Postfächer…" className="justify-start" />
            ) : accounts.length === 0 ? (
              <p className="text-sm text-app-text-subtle">Noch kein Postfach verbunden.</p>
            ) : (
                <ul className="-mb-1 max-h-[320px] divide-y divide-app-border overflow-y-auto app-scrollbar">
                  {accounts.map((acc) => {
                    const accountId = String(acc.id);
                    const isConnected = isAccountConnected(acc);
                    const hasSignature = Boolean(String(acc.signature_html ?? '').trim());
                    const addresses = mailboxFromChoices(acc.email, acc.email_aliases);

                    return (
                      <li key={acc.id} className="py-2 first:pt-0 last:pb-1">
                        {addresses.map((email, index) => {
                          const isAlias = index > 0;
                          const menuKey = `${accountId}::${isAlias ? 'alias' : 'primary'}::${email}`;
                            const isStandard = addressIsStandard(accountId, email, acc.email, Boolean(acc.is_private));
                          const menuOpen = openMenuId === menuKey;
                          return (
                            <div
                              key={email}
                              className={`flex items-center gap-3 ${isAlias ? 'mt-1 pl-5' : ''}`}
                            >
                              {isAlias ? (
                                <span className="flex h-4 w-2 shrink-0 items-center text-app-text-subtle" aria-hidden>
                                  <span className="block h-2.5 w-2 rounded-bl border-b border-l border-current" />
                                </span>
                              ) : (
                                <span
                                  className={`h-2 w-2 shrink-0 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-amber-400'}`}
                                  title={isConnected ? 'Verbunden' : 'Nicht verbunden'}
                                />
                              )}
                              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                                <span className={`truncate text-sm ${isAlias ? 'text-app-text-muted' : 'font-medium text-app-text'}`}>
                                  {email}
                                </span>
                                {isAlias ? (
                                  <span className="shrink-0 rounded-md bg-app-surface-muted px-2 py-0.5 text-[11px] font-medium text-app-text-muted">
                                    Alias
                                  </span>
                                ) : (
                                  <span className="shrink-0 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-pipedrive-blue">
                                    {providerLabel(acc.provider)}
                                  </span>
                                )}
                                {!isAlias && acc.is_private ? (
                                  <span className="shrink-0 rounded-md bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">
                                    Privat
                                  </span>
                                ) : null}
                                {isStandard ? (
                                  <span className="shrink-0 rounded-md bg-app-surface-muted px-2 py-0.5 text-[11px] font-medium text-app-text-muted">
                                    Standard
                                  </span>
                                ) : null}
                                {!isAlias && !isConnected ? (
                                  <span className="text-[11px] font-medium text-amber-700">Nicht verbunden</span>
                                ) : null}
                              </div>
                              {!isAlias && hasSignature ? (
                                <span className="shrink-0 text-xs font-medium text-pipedrive-blue">
                                  Signatur vorhanden
                                </span>
                              ) : null}
                              {!isAlias || (isConnected && !acc.is_private) ? (
                              <div className="relative shrink-0">
                                <button
                                  type="button"
                                  ref={(el) => {
                                    if (el) menuTriggerRefs.current.set(menuKey, el);
                                    else menuTriggerRefs.current.delete(menuKey);
                                  }}
                                  onClick={() => setOpenMenuId((current) => (current === menuKey ? null : menuKey))}
                                  className="rounded-lg p-1.5 text-slate-400 hover:bg-app-hover hover:text-app-text-muted transition-colors"
                                  aria-label="Aktionen"
                                  aria-expanded={menuOpen}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </li>
                    );
                  })}
                </ul>
              )}
          </div>
        )}
      </SettingsCard>

      {openMenuAccount && menuPos && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuPanelRef}
              style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 300 }}
              className="min-w-[220px] rounded-xl border border-app-border bg-app-surface p-1 shadow-lg"
            >
              {isAccountConnected(openMenuAccount) && mailDispatchLoaded && !openMenuAccount.is_private ? (
                <button
                  type="button"
                  onClick={() =>
                    beginMailDispatchForAccount(
                      String(openMenuAccount.id),
                      openMenuTarget?.email || openMenuAccount.email,
                    )
                  }
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
                >
                  <Mail className="h-4 w-4 text-app-text-subtle" />
                  Versandart festlegen
                </button>
              ) : null}
              {isAccountConnected(openMenuAccount) && mailDispatchLoaded && !openMenuAccount.is_private ? (
                <button
                  type="button"
                  onClick={() => void handleSetAsStandard(openMenuAccount, openMenuTarget?.email || openMenuAccount.email)}
                  disabled={mailDispatchSaving}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                >
                  <Star className="h-4 w-4 text-app-text-subtle" />
                  Als Standard festlegen
                </button>
              ) : null}
              {!openMenuTarget?.isAlias && openMenuAccount.is_private ? (
                <div className="px-3 py-2 text-xs text-app-text-subtle">
                  Private Postfächer können nicht als Standard-Absender verwendet werden.
                </div>
              ) : null}
              {!openMenuTarget?.isAlias &&
              currentUserId &&
              String(openMenuAccount.owner_user_id ?? '') === String(currentUserId) ? (
                <button
                  type="button"
                  onClick={() => requestVisibilityChange(openMenuAccount)}
                  disabled={visibilitySaving}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                >
                  {openMenuAccount.is_private ? (
                    <LockOpen className="h-4 w-4 text-app-text-subtle" />
                  ) : (
                    <Lock className="h-4 w-4 text-app-text-subtle" />
                  )}
                  {openMenuAccount.is_private ? 'Auf allgemein umstellen' : 'Auf privat umstellen'}
                </button>
              ) : null}
              {!openMenuTarget?.isAlias && openMenuAccount.nylas_grant_id ? (
                <button
                  type="button"
                  onClick={() => handleCredentialsChange(openMenuAccount)}
                  disabled={adding}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                >
                  <KeyRound className="h-4 w-4 text-app-text-subtle" />
                  {isGmailProvider(openMenuAccount.provider)
                    ? 'Bei Google erneut anmelden'
                    : 'Passwort erneut eingeben'}
                </button>
              ) : null}
              {!openMenuTarget?.isAlias && inboxEnabled ? (
              <button
                type="button"
                onClick={() => {
                  setOpenMenuId(null);
                  handleSync(openMenuAccount.id);
                }}
                disabled={syncingId != null && String(syncingId) === String(openMenuAccount.id)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-50"
              >
                {syncingId != null && String(syncingId) === String(openMenuAccount.id) ? (
                  <LoadingSpinner className="text-app-text-subtle" />
                ) : (
                  <RefreshCw className="h-4 w-4 text-app-text-subtle" />
                )}
                Jetzt synchronisieren
              </button>
              ) : null}
              {!openMenuTarget?.isAlias ? (
              <>
              <div className="my-1 border-t border-app-border" />
              <button
                type="button"
                onClick={() => {
                  setOpenMenuId(null);
                  setRemoveId(openMenuAccount.id);
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Entfernen
              </button>
              </>
              ) : null}
            </div>,
            document.body,
          )
        : null}

      {privacyConfirmAccount ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            if (!visibilitySaving) {
              setPrivacyConfirmAccount(null);
              setPrivacyReplacementAccountId('');
            }
          }}
        >
          <div
            className="relative w-full max-w-md rounded-xl border border-app-border bg-app-surface p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              disabled={visibilitySaving}
              onClick={() => {
                setPrivacyConfirmAccount(null);
                setPrivacyReplacementAccountId('');
              }}
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="pr-8">
              <h3 className="text-lg font-semibold text-app-text">Auf privat umstellen?</h3>
              <p className="mt-1 truncate text-sm text-app-text-muted">
                {privacyConfirmAccount.email}
              </p>
            </div>

            <div className="mt-4">
              <p className={SETTINGS_FIELD_LABEL}>Was sich ändert</p>
              <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-app-text-muted">
                <li className="flex gap-2">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
                  <span>Nur Sie sehen dieses Postfach und seine E-Mails.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
                  <span>Kollegen verlieren den Zugriff in Posteingang, Projekt-E-Mails und KI-Suche.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
                  <span>Als Standard-Absender kann es nicht mehr verwendet werden.</span>
                </li>
              </ul>
            </div>

            {privacyNeedsReplacement ? (
              <div className="mt-4 space-y-3">
                {privacyAffectedPurposes.length > 0 ? (
                  <div>
                    <p className={SETTINGS_FIELD_LABEL}>Betroffene Versandarten</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {privacyAffectedPurposes.map((purpose) => (
                        <span
                          key={purpose}
                          className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-pipedrive-blue"
                        >
                          {MAIL_DISPATCH_PURPOSE_LABELS[purpose]}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {privacyIsExplicitDefault ? (
                  <p className="text-sm text-app-text-muted">Dieses Postfach ist der Standard-Absender.</p>
                ) : null}

                {privacyReplacementOptions.length > 0 ? (
                  <div>
                    <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                      Neuen Standard-Absender wählen
                    </label>
                    <AppSelectDropdown
                      value={privacyReplacementAccountId}
                      onChange={setPrivacyReplacementAccountId}
                      options={privacyReplacementOptions.map((acc) => ({
                        value: String(acc.id),
                        label: acc.email,
                      }))}
                      placeholder="Postfach wählen …"
                      className="w-full min-w-0"
                      triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                      disabled={visibilitySaving}
                    />
                    <p className="mt-1.5 text-xs text-app-text-subtle">
                      {privacyAffectedPurposes.length > 0 && privacyIsExplicitDefault
                        ? 'Dieses Postfach übernimmt die oben genannten Versandarten und wird der neue Standard-Absender.'
                        : privacyIsExplicitDefault
                          ? 'Dieses Postfach wird der neue Standard-Absender.'
                          : 'Dieses Postfach übernimmt die oben genannten Versandarten.'}
                    </p>
                  </div>
                ) : (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Kein anderes allgemeines Postfach verfügbar. Die betroffenen Versandarten bleiben ohne
                    Standard-Absender.
                  </p>
                )}
              </div>
            ) : null}

            <div className="mt-6 flex flex-col-reverse gap-2 border-t border-app-border pt-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={visibilitySaving}
                onClick={() => {
                  setPrivacyConfirmAccount(null);
                  setPrivacyReplacementAccountId('');
                }}
                className={`w-full sm:w-auto ${SETTINGS_BTN_OUTLINE}`}
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={
                  visibilitySaving ||
                  (privacyNeedsReplacement &&
                    privacyReplacementOptions.length > 0 &&
                    !privacyReplacementAccountId)
                }
                onClick={() =>
                  void applyAccountVisibility(privacyConfirmAccount, true, {
                    replacementAccountId: privacyNeedsReplacement
                      ? privacyReplacementAccountId || null
                      : null,
                  })
                }
                className={`w-full sm:w-auto ${SETTINGS_BTN_PRIMARY}`}
              >
                {visibilitySaving ? 'Speichert…' : 'Privat machen'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSyncSinceModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => {
            setShowSyncSinceModal(false);
            setPendingSyncAccountId(null);
          }}
        >
          <div
            className="bg-app-surface rounded-xl shadow-lg border border-app-border p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-app-text mb-2">E-Mails abrufen</h3>
            <p className="text-sm text-app-text-muted mb-4">
              Ab wann sollen E-Mails synchronisiert werden? Es werden nur E-Mails ab diesem Datum abgerufen.
            </p>
            <DatePicker
              value={syncSinceDate}
              onChange={setSyncSinceDate}
              className="w-full mb-4"
              placeholder="TT.MM.JJJJ"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowSyncSinceModal(false);
                  setPendingSyncAccountId(null);
                }}
                className={SETTINGS_BTN_OUTLINE}
              >
                Abbrechen
              </button>
              <button type="button" onClick={handleSyncSinceConfirm} className={SETTINGS_BTN_PRIMARY}>
                Abrufen
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {reauthImapAccount ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => {
            setReauthImapAccount(null);
            setImapForm(emptyImapForm());
          }}
        >
          <div
            className="bg-app-surface rounded-xl shadow-lg border border-app-border p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-app-text mb-2">Postfach erneut verbinden</h3>
            <p className="text-sm text-app-text-muted mb-4">
              Passwort für <span className="font-medium">{reauthImapAccount.email}</span> erneut eingeben.
            </p>
            <div className="space-y-3">
              <div>
                <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Passwort</label>
                <input
                  type="password"
                  value={imapForm.password}
                  onChange={(e) => setImapForm((prev) => ({ ...prev, password: e.target.value }))}
                  className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
                  autoComplete="off"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => setImapForm((prev) => ({ ...prev, showAdvanced: !prev.showAdvanced }))}
                  className="text-sm font-medium text-pipedrive-blue hover:underline"
                >
                  {imapForm.showAdvanced ? 'Erweiterte Einstellungen ausblenden' : 'Erweiterte Einstellungen'}
                </button>
              </div>
              {imapForm.showAdvanced ? (
                <div className="grid gap-3 sm:grid-cols-2 rounded-lg border border-app-border bg-app-surface-muted p-3">
                  <div>
                    <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>IMAP-Host</label>
                    <input
                      type="text"
                      value={imapForm.imap_host}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, imap_host: e.target.value }))}
                      className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
                    />
                  </div>
                  <div>
                    <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>IMAP-Port</label>
                    <input
                      type="text"
                      value={imapForm.imap_port}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, imap_port: e.target.value }))}
                      className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
                    />
                  </div>
                  <div>
                    <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>SMTP-Host</label>
                    <input
                      type="text"
                      value={imapForm.smtp_host}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, smtp_host: e.target.value }))}
                      className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
                    />
                  </div>
                  <div>
                    <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>SMTP-Port</label>
                    <input
                      type="text"
                      value={imapForm.smtp_port}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, smtp_port: e.target.value }))}
                      className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                type="button"
                onClick={() => {
                  setReauthImapAccount(null);
                  setImapForm(emptyImapForm());
                }}
                className={SETTINGS_BTN_OUTLINE}
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={adding}
                onClick={() => void handleConnectImap({ reauthAccount: reauthImapAccount })}
                className={SETTINGS_BTN_PRIMARY}
              >
                {adding ? 'Verbinde…' : 'Verbinden'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {removeId !== null ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
          onClick={() => setRemoveId(null)}
        >
          <div
            className="bg-app-surface rounded-xl shadow-2xl max-w-md w-[90vw] border border-app-border text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-app-border">
              <p className="text-sm font-semibold text-app-text">E-Mail-Konto wirklich entfernen?</p>
              <p className="text-xs text-app-text-subtle mt-2">
                Gespeicherte E-Mails dieses Kontos werden aus HeizungPro gelöscht. Im eigentlichen Postfach bleiben sie
                weiterhin bestehen.
              </p>
            </div>
            <div className="px-6 py-4 flex items-center justify-center gap-3">
              <button type="button" onClick={() => setRemoveId(null)} className={SETTINGS_BTN_OUTLINE}>
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => {
                  const id = removeId;
                  setRemoveId(null);
                  if (id == null) return;
                  fetch(`/api/inbox/accounts/${id}`, { method: 'DELETE' })
                    .then(() => onReload())
                    .catch((err) => onError(err.message || 'Konto konnte nicht entfernt werden'));
                }}
                className={SETTINGS_BTN_PRIMARY}
              >
                Konto entfernen
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
